import { useState, useMemo, useCallback, useEffect } from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Input,
  Button,
  Switch,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DataTable,
  membershipTableConfig,
} from "@b2b/ui-components";

import { membershipApi, MembershipPlan, CreatePlanDto } from "@b2b/services";

import {
  Plus,
  Edit,
  Trash2,
  Copy,
  Eye,
  Check,
  X,
  Crown,
  Star,
  Users,
  Package,
  TrendingUp,
} from "lucide-react";

// Helper function to normalize features to always be an array
const normalizeFeatures = (features: any): string[] => {
  if (Array.isArray(features)) {
    return features.map(f => typeof f === 'string' ? f : String(f));
  }
  if (typeof features === 'string') {
    try {
      const parsed = JSON.parse(features);
      return Array.isArray(parsed) ? parsed.map(f => typeof f === 'string' ? f : String(f)) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const sanitizeNonNegativeDecimal = (value: string): string =>
  value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");

const sanitizeNonNegativeInteger = (value: string): string =>
  value.replace(/\D/g, "");

export default function AdminMembership() {
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewPlan, setPreviewPlan] = useState<MembershipPlan | null>(null);
  const [editingPlan, setEditingPlan] = useState<MembershipPlan | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Load plans on component mount
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        const plansData = await membershipApi.getAllPlans();
        // Ensure plansData is an array and normalize features
        const normalizedPlans = Array.isArray(plansData)
          ? plansData.map((plan) => ({
              ...plan,
              features: normalizeFeatures(plan.features),
            }))
          : [];
        setPlans(normalizedPlans);
      } catch (error) {
        console.error("Failed to load plans:", error);
        // Set empty array on error to prevent filter errors
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, []);

  const planStats = useMemo(() => {
    // Ensure plans is an array before using array methods
    if (!Array.isArray(plans)) {
      return {
        total: 0,
        active: 0,
        recommendedPlanName: "None",
        priceRange: "N/A",
      };
    }

    const activePlans = plans.filter(
      (p) => p && typeof p.isActive === "boolean" && p.isActive
    );
    const recommended = plans.find(
      (p) => p && typeof p.isPopular === "boolean" && p.isPopular
    );
    const prices = plans
      .map((p) => (p && typeof p.price === "number" ? p.price : 0))
      .filter((p) => p);

    if (prices.length === 0) {
      return {
        total: plans.length,
        active: activePlans.length,
        recommendedPlanName: recommended ? recommended.name : "None",
        priceRange: "N/A",
      };
    }

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    return {
      total: plans.length,
      active: activePlans.length,
      recommendedPlanName: recommended ? recommended.name : "None",
      priceRange: `${minPrice} - ${maxPrice}`,
    };
  }, [plans]);

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    currency: "USD",
    billingCycle: "monthly" as "monthly" | "yearly",
    features: [""],
    isActive: true,
    isPopular: false,
    maxProducts: "",
    maxUsers: "",
    description: "",
  });
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    price?: string;
    maxProducts?: string;
    maxUsers?: string;
  }>({});

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      price: "",
      currency: "USD",
      billingCycle: "monthly",
      features: [""],
      isActive: true,
      isPopular: false,
      maxProducts: "",
      maxUsers: "",
      description: "",
    });
    setFormErrors({});
  }, []);

  const validateForm = useCallback(() => {
    const errors: {
      name?: string;
      price?: string;
      maxProducts?: string;
      maxUsers?: string;
    } = {};

    if (!formData.name.trim()) {
      errors.name = "Plan name is required";
    }

    if (!formData.price.trim()) {
      errors.price = "Price is required";
    } else {
      const price = Number(formData.price);
      if (!Number.isFinite(price) || price < 0) {
        errors.price = "Price must be a non-negative number";
      }
    }

    if (formData.maxProducts.trim()) {
      const maxProducts = Number(formData.maxProducts);
      if (!Number.isInteger(maxProducts) || maxProducts < 0) {
        errors.maxProducts = "Max products must be a non-negative whole number";
      }
    }

    if (formData.maxUsers.trim()) {
      const maxUsers = Number(formData.maxUsers);
      if (!Number.isInteger(maxUsers) || maxUsers < 0) {
        errors.maxUsers = "Max users must be a non-negative whole number";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData.maxProducts, formData.maxUsers, formData.name, formData.price]);

  const handleAddFeature = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  }, []);

  const handleRemoveFeature = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  }, []);

  const handleFeatureChange = useCallback((index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.map((feature, i) =>
        i === index ? value : feature
      ),
    }));
  }, []);

  const handleSavePlan = useCallback(async () => {
    if (!validateForm()) return;

    try {
      const planData: CreatePlanDto = {
        name: formData.name,
        price: parseFloat(formData.price),
        currency: formData.currency,
        billingCycle: formData.billingCycle,
        features: formData.features.filter((f) => f.trim() !== ""),
        isActive: formData.isActive,
        isPopular: formData.isPopular,
        maxProducts: formData.maxProducts
          ? parseInt(formData.maxProducts)
          : undefined,
        maxUsers: formData.maxUsers ? parseInt(formData.maxUsers) : undefined,
        description: formData.description || undefined,
      };

      if (editingPlan) {
        const updatedPlan = await membershipApi.updatePlan(
          editingPlan.id,
          planData
        );
        // Normalize features in the updated plan
        setPlans((prev) =>
          prev.map((p) => (p.id === editingPlan.id 
            ? { ...updatedPlan, features: normalizeFeatures(updatedPlan.features) } 
            : p))
        );
      } else {
        const newPlan = await membershipApi.createPlan(planData);
        // Normalize features in the new plan
        setPlans((prev) => [...prev, { ...newPlan, features: normalizeFeatures(newPlan.features) }]);
      }

      setIsAddModalOpen(false);
      setEditingPlan(null);
      resetForm();
    } catch (error) {
      console.error("Failed to save plan:", error);

      // Handle specific error cases
      if (error instanceof Error) {
        if (error.message.includes("409")) {
          alert(
            "A plan with this name already exists. Please choose a different name."
          );
        } else if (error.message.includes("401")) {
          alert(
            "You are not authorized to perform this action. Please log in."
          );
        } else if (error.message.includes("403")) {
          alert("You do not have permission to create plans.");
        } else {
          alert(`Failed to save plan: ${error.message}`);
        }
      } else {
        alert("An unexpected error occurred. Please try again.");
      }
    }
  }, [formData, editingPlan, resetForm, validateForm]);

  const handleEditPlan = useCallback((plan: MembershipPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      price: plan.price.toString(),
      currency: plan.currency,
      billingCycle: plan.billingCycle,
      features: Array.isArray(plan.features) && plan.features.length > 0 
        ? plan.features 
        : [""],
      isActive: plan.isActive,
      isPopular: plan.isPopular,
      maxProducts: plan.maxProducts?.toString() || "",
      maxUsers: plan.maxUsers?.toString() || "",
      description: plan.description || "",
    });
    setIsAddModalOpen(true);
  }, []);

  const handleDeletePlan = useCallback(async (planId: string) => {
    // Find the plan to show its name in confirmation
    const planToDelete = plans.find((p) => p.id === planId);
    const planName = planToDelete?.name || "this plan";

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete "${planName}"?\n\nThis action cannot be undone. If this plan has active subscriptions, it cannot be deleted.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await membershipApi.deletePlan(planId);
      // Remove the plan from state
      setPlans((prev) => prev.filter((p) => p.id !== planId));
      // Show success message
      alert(`Plan "${planName}" has been deleted successfully.`);
    } catch (error) {
      console.error("Failed to delete plan:", error);
      
      // Show user-friendly error message
      let errorMessage = "Failed to delete plan. Please try again.";
      if (error instanceof Error) {
        if (error.message.includes("409") || error.message.includes("active subscriptions")) {
          errorMessage = `Cannot delete "${planName}" because it has active subscriptions. Please cancel all subscriptions first.`;
        } else if (error.message.includes("404")) {
          errorMessage = `Plan "${planName}" not found. It may have already been deleted.`;
        } else if (error.message.includes("403")) {
          errorMessage = "You do not have permission to delete plans.";
        } else if (error.message.includes("401")) {
          errorMessage = "You are not authorized. Please log in again.";
        } else {
          errorMessage = `Failed to delete plan: ${error.message}`;
        }
      }
      alert(errorMessage);
    }
  }, [plans]);

  const handleDuplicatePlan = useCallback(async (plan: MembershipPlan) => {
    try {
      const duplicatedPlanData: CreatePlanDto = {
        name: `${plan.name} (Copy)`,
        price: plan.price,
        currency: plan.currency,
        billingCycle: plan.billingCycle,
        features: normalizeFeatures(plan.features),
        isActive: false,
        isPopular: false,
        maxProducts: plan.maxProducts,
        maxUsers: plan.maxUsers,
        description: plan.description,
      };

      const newPlan = await membershipApi.createPlan(duplicatedPlanData);
      // Normalize features in the new plan before adding to state
      setPlans((prev) => [...prev, { ...newPlan, features: normalizeFeatures(newPlan.features) }]);
    } catch (error) {
      console.error("Failed to duplicate plan:", error);
    }
  }, []);

  const handlePreview = useCallback((plan: MembershipPlan) => {
    setPreviewPlan(plan);
    setIsPreviewModalOpen(true);
  }, []);

  const filteredPlans = useMemo(() => {
    // Ensure plans is an array before filtering
    if (!Array.isArray(plans)) {
      return [];
    }

    return plans.filter((plan) => {
      // Safety check for plan object and its properties
      if (!plan || typeof plan.name !== "string") {
        return false;
      }

      const matchesSearch = plan.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? plan.isActive : !plan.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [plans, searchTerm, statusFilter]);

  const formatPrice = (price: number, currency: string, billingCycle: string) =>
    `${price}/${billingCycle === "monthly" ? "mo" : "yr"}`;

  const tableData = useMemo(
    () =>
      filteredPlans
        .filter((plan) => plan && typeof plan.name === "string")
        .map((plan) => ({
          name: (
            <div className="flex items-center gap-2">
              {plan.name}
              {plan.isPopular && (
                <Badge
                  variant="secondary"
                  className="bg-orange-100 text-orange-500"
                >
                  <Crown className="h-3 w-3 mr-1" />
                  Popular
                </Badge>
              )}
            </div>
          ),
          price: formatPrice(plan.price, plan.currency, plan.billingCycle),
          billingCycle: plan.billingCycle,
          features: `${Array.isArray(plan.features) ? plan.features.length : 0} features`,
          status: plan.isActive ? "active" : "inactive",
          actions: (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePreview(plan)}
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEditPlan(plan)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              {/* <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDuplicatePlan(plan)}
              >
                <Copy className="h-4 w-4" />
              </Button> */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeletePlan(plan.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ),
        })),
    [
      filteredPlans,
      handlePreview,
      handleEditPlan,
      handleDuplicatePlan,
      handleDeletePlan,
    ]
  );

  // Show loading state while plans are being fetched
  if (loading) {
    return (
      <div className="flex-1 space-y-6 p-8 pt-6">
        <div className="flex items-center justify-center h-64">
          <p>Loading membership plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-8 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            Membership Management
          </h2>
          <p className=" text-gray-500">
            Manage subscription plans and preview how they appear to customers
          </p>
        </div>
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogTrigger asChild>
            <Button variant="primary" onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Add New Plan
            </Button>
          </DialogTrigger>
          <DialogContent
            className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white"
            showCloseButton={false}
          >
            <DialogHeader>
              <DialogTitle>
                {editingPlan ? "Edit Plan" : "Create New Plan"}
              </DialogTitle>
              <DialogDescription>
                {editingPlan
                  ? "Update plan details"
                  : "Add a new membership plan for your marketplace"}
              </DialogDescription>
            </DialogHeader>
            {/* Form */}
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="mb-2">
                    Plan Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, name: e.target.value }));
                      setFormErrors((prev) => ({ ...prev, name: undefined }));
                    }}
                    placeholder="e.g., Professional"
                    className={formErrors.name ? "border-red-500" : ""}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="price" className="mb-2">
                    Price <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="price"
                    type="text"
                    inputMode="decimal"
                    pattern="^\d*\.?\d*$"
                    value={formData.price}
                    onChange={(e) => {
                      const sanitized = sanitizeNonNegativeDecimal(e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        price: sanitized,
                      }));
                      setFormErrors((prev) => ({ ...prev, price: undefined }));
                    }}
                    placeholder="99"
                    className={formErrors.price ? "border-red-500" : ""}
                  />
                  {formErrors.price && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.price}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="mb-2">
                  Description
                </Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Plan description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="currency" className="mb-2">
                    Currency
                  </Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, currency: value }))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="USD" className="hover:bg-gray-100">
                        USD
                      </SelectItem>
                      <SelectItem value="EUR" className="hover:bg-gray-100">
                        EUR
                      </SelectItem>
                      <SelectItem value="GBP" className="hover:bg-gray-100">
                        GBP
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="billing" className="mb-2">
                    Billing Cycle
                  </Label>
                  <Select
                    value={formData.billingCycle}
                    onValueChange={(value: "monthly" | "yearly") =>
                      setFormData((prev) => ({ ...prev, billingCycle: value }))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="monthly" className="hover:bg-gray-100">
                        Monthly
                      </SelectItem>
                      <SelectItem value="yearly" className="hover:bg-gray-100">
                        Yearly
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="maxProducts" className="mb-2">
                    Max Products
                  </Label>
                  <Input
                    id="maxProducts"
                    type="text"
                    inputMode="numeric"
                    pattern="^\d*$"
                    value={formData.maxProducts}
                    onChange={(e) => {
                      const sanitized = sanitizeNonNegativeInteger(e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        maxProducts: sanitized,
                      }));
                      setFormErrors((prev) => ({ ...prev, maxProducts: undefined }));
                    }}
                    placeholder="Unlimited"
                  />
                  {formErrors.maxProducts && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.maxProducts}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="maxUsers" className="mb-2">
                    Max Users
                  </Label>
                  <Input
                    id="maxUsers"
                    type="text"
                    inputMode="numeric"
                    pattern="^\d*$"
                    value={formData.maxUsers}
                    onChange={(e) => {
                      const sanitized = sanitizeNonNegativeInteger(e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        maxUsers: sanitized,
                      }));
                      setFormErrors((prev) => ({ ...prev, maxUsers: undefined }));
                    }}
                    placeholder="Unlimited"
                  />
                  {formErrors.maxUsers && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.maxUsers}</p>
                  )}
                </div>
              </div>

              <div>
                <Label className="mb-2">Features</Label>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) =>
                          handleFeatureChange(index, e.target.value)
                        }
                        placeholder="Enter feature description"
                      />
                      {formData.features.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveFeature(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddFeature}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Feature
                  </Button>
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        isActive: checked,
                      }))
                    }
                  />
                  <Label htmlFor="isActive">Active Plan</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isPopular"
                    checked={formData.isPopular}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        isPopular: checked,
                      }))
                    }
                  />
                  <Label htmlFor="isPopular">Popular Plan</Label>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setEditingPlan(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleSavePlan} variant="primary">
                  {editingPlan ? "Update Plan" : "Create Plan"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Plans</CardTitle>
            <Package className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{planStats.total}</div>
            <p className="text-xs text-gray-500">{planStats.active} active</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Recommended Plan
            </CardTitle>
            <Crown className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {planStats.recommendedPlanName}
            </div>
            <p className="text-xs text-gray-500">Most popular choice</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Price Range</CardTitle>
            <TrendingUp className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{planStats.priceRange}</div>
            <p className="text-xs text-gray-500">Based on all plans</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Subscribers
            </CardTitle>
            <Users className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            {/* This value would typically come from a separate API endpoint */}
            <div className="text-2xl font-bold">2,847</div>
            <p className="text-xs text-gray-500">+12% from last month</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Plan Management</CardTitle>
          <CardDescription>
            Manage your membership plans, edit details, and preview customer
            view
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Input
                placeholder="Search plans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem
                  value="all"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  All Status
                </SelectItem>
                <SelectItem
                  value="active"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  Active
                </SelectItem>
                <SelectItem
                  value="inactive"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  Inactive
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Plans DataTable */}
          <div className="rounded-md border">
            <DataTable
              data={tableData}
              config={membershipTableConfig}
              rowsPerPage={5}
            />
          </div>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={isPreviewModalOpen} onOpenChange={setIsPreviewModalOpen}>
        <DialogContent className="max-w-4xl bg-white">
          <DialogHeader>
            <DialogTitle>Plan Preview - Customer View</DialogTitle>
            <DialogDescription className="text-gray-500">
              This is how the "{previewPlan?.name}" plan will appear to
              customers
            </DialogDescription>
          </DialogHeader>

          {previewPlan && (
            <div className="grid md:grid-cols-3 gap-6 py-6">
              {/* Show the previewed plan alongside two others for context */}
              {plans
                .filter((p) => p.isActive)
                .slice(0, 3)
                .map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative rounded-lg border p-6 ${
                      plan.id === previewPlan.id
                        ? "border-orange-500 shadow-lg transform scale-105"
                        : "border-border"
                    } ${plan.isPopular ? "ring-2 ring-orange-500" : ""}`}
                  >
                    {plan.isPopular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-orange-600 text-white px-3 py-1">
                          <Star className="h-3 w-3 mr-1" />
                          Most Popular
                        </Badge>
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                      <div className="text-3xl font-bold text-orange-500">
                        {formatPrice(
                          plan.price,
                          plan.currency,
                          plan.billingCycle
                        )}
                      </div>
                      <p className="text-gray-500 capitalize">
                        Billed {plan.billingCycle}
                      </p>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {Array.isArray(plan.features) && plan.features.length > 0 ? (
                        plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-green-500" />
                            <span className="text-sm">
                              {typeof feature === 'string' ? feature : String(feature)}
                            </span>
                          </li>
                        ))
                      ) : (
                        <li className="text-sm text-muted-foreground">
                          No features available
                        </li>
                      )}
                    </ul>

                    <Button
                      className="w-full"
                      variant={plan.isPopular ? "primary" : "outline"}
                    >
                      {plan.isPopular ? "Upgrade Now" : "Choose Plan"}
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
